import type { MaybeRefOrGetter } from 'vue';
import { toValue, watch } from 'vue';

import type { UserLanguagePreference } from './useLanguagePreferenceSync';
import { useLanguagePreferenceSync } from './useLanguagePreferenceSync';

type AuthenticatedUser = UserLanguagePreference & { id: string };

/**
 * Applies {@link syncLanguagePreferenceOnLogin} whenever the authenticated user becomes available
 * or is refreshed (e.g. after `Auth/getMe` commits `Auth/me`).
 */
export function useLanguagePreferenceLoginWatcher(
  authenticatedUser: MaybeRefOrGetter<AuthenticatedUser | null | undefined>,
  options?: {
    onApplied?: () => void;
  }
) {
  const { syncLanguagePreferenceOnLogin } = useLanguagePreferenceSync(
    () => toValue(authenticatedUser)?.id
  );

  async function syncFromAuthenticatedUser(user: AuthenticatedUser | null | undefined) {
    if (!user?.id) {
      return;
    }

    await syncLanguagePreferenceOnLogin(user);
    options?.onApplied?.();
  }

  watch(
    () => toValue(authenticatedUser),
    user => {
      void syncFromAuthenticatedUser(user);
    },
    { immediate: true }
  );
}
